<template>
  <BContainer
    v-if="user"
    fluid
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BTabs
      v-model="currentTabIndex"
      active-nav-item-class="font-weight-bold text-uppercase text-primary"
      class="mt-3"
    >
      <BTab>
        <template #title>
          <i :class="iconState('fa-file-pen', currentTabIndex === 0)" /> Pendings
        </template>
        <ApprovalPending
          ref="saPending"
          :tab-index="currentTabIndex"
          @show="showApproval"
        />
      </BTab>

      <BTab>
        <template #title>
          <i :class="iconState('fa-clock-rotate-left', currentTabIndex === 1)" /> Histories
        </template>
        <ApprovalHistory
          ref="saHistory"
          :tab-index="currentTabIndex"
          @show="showApproval"
        />
      </BTab>
    </BTabs>

    <BModal
      id="modalApproval"
      size="xl"
      scrollable
      centered
      hide-header
      no-enforce-focus
      no-close-on-backdrop
      content-class="scrollable-tabs"
      modal-class="full-height modal-no-padding"
      aria-hidden="false"
      role="dialog"
    >
      <BTabs
        active-nav-item-class="font-weight-bold text-uppercase text-primary"
        content-class="scrollable"
      >
        <BTab class="p-2">
          <template #title>
            <div class="text-md-nowrap">
              <i class="fa-lg fad fa-list-tree mr-1" />
              <b class="is-tab-active">Products</b>
            </div>
          </template>
          <BRow class="mx-0 mt-1 mb-0">
            <BCol
              cols="12"
              class="px-1 px-md-2"
            >
              <BRow>
                <BCol
                  cols="12"
                  md="3"
                  class="px-1 px-md-2"
                >
                  <BFormGroup
                    label="Transaction Number"
                    class="mb-2"
                  >
                    <strong
                      v-text="stockRequest.references.transaction_number"
                    />
                  </BFormGroup>
                </BCol>

                <BCol
                  cols="12"
                  md="3"
                  class="px-1 px-md-2"
                >
                  <BFormGroup
                    label="Ordered By"
                    class="mb-2"
                  >
                    <strong
                      v-text="stockRequest.headers.destination_name"
                    />
                  </BFormGroup>
                </BCol>

                <BCol
                  cols="12"
                  md="3"
                  class="px-1 px-md-2"
                >
                  <BFormGroup
                    label="Group"
                    class="mb-2"
                  >
                    <strong
                      v-text="stockRequest.headers.group_name"
                    />
                  </BFormGroup>
                </BCol>

                <BCol
                  cols="12"
                  class="px-1 px-md-2"
                >
                  <BFormGroup
                    label="Remarks"
                    class="mb-2"
                  >
                    <strong
                      v-text="stockRequest.remarks || 'no remarks'"
                    />
                  </BFormGroup>
                </BCol>

                <BCol
                  v-if="state.approval"
                  cols="12"
                  class="px-1 px-md-2 d-flex justify-content-end align-items-end"
                >
                  <div class="mb-2">
                    <BButton
                      v-b-tooltip.hover.left="{ variant: 'primary' }"
                      title="Query Stocks from SAP"
                      size="sm"
                      variant="outline-primary"
                      tabindex="-1"
                      @click.prevent="getStocks"
                    >
                      <i
                        :class="['fa-lg fad fa-arrows-rotate pr-0', { 'fa-spin': fetching.stocks }]"
                      />
                    </BButton>
                  </div>
                </BCol>
              </BRow>
            </BCol>

            <BCol
              cols="12"
              class="px-0 px-md-1"
            >
              <ValidationObserver
                ref="formProducts"
              >
                <BTable
                  ref="tableProducts"
                  small
                  hover
                  striped
                  bordered
                  responsive
                  show-empty
                  class="mb-0"
                  :items="stockRequest.details"
                  :busy="tableProducts.busy"
                  :filter="tableProducts.filter"
                  :fields="state.approval ? tableProducts.fields : tableProducts.histories"
                  :stacked="tableProducts.stacked"
                  :filter-included-fields="tableProducts.filterOn"
                >
                  <template #cell(index)="{ index }">
                    <div
                      class="text-md-nowrap"
                      v-text="tableRowNumber(tableProducts, index)"
                    />
                  </template>

                  <template #cell()="{ value }">
                    <div
                      class="text-md-nowrap d-flex align-items-center min-h-35px px-1"
                      v-text="value"
                    />
                  </template>

                  <template #cell(product_name)="{ value }">
                    <div class="text-md-nowrap d-flex align-items-center min-h-35px px-1">
                      <strong
                        class="font-weight-bolder"
                        v-text="value"
                      />
                    </div>
                  </template>

                  <template #cell(onhand_quantity)="{ value }">
                    <div class="text-md-nowrap d-flex justify-content-center align-items-center min-h-35px">
                      <strong
                        :class="['font-weight-bolder', {
                          'text-danger': Number(value) === 0
                        }]"
                        v-text="numberFormatter(value, '0,0')"
                      />
                    </div>
                  </template>

                  <template #cell(quantity)="{ value }">
                    <div class="text-md-nowrap d-flex justify-content-center align-items-center min-h-35px">
                      <strong
                        class="font-weight-bolder"
                        v-text="numberFormatter(value, '0,0')"
                      />
                    </div>
                  </template>

                  <template #cell(approved_quantity)="{ item, index }">
                    <div
                      v-if="state.approval"
                      class="text-md-nowrap"
                    >
                      <ValidationProvider
                        v-slot="{ errors }"
                        :vid="`details.${index}.quantity`"
                        :rules="{
                          required: true,
                          min_value: 0,
                          max_value: item.quantity
                        }"
                        name="quantity"
                      >
                        <BFormGroup
                          :label-for="`fApprovedQuantity-${index}`"
                          class="mb-0"
                        >
                          <BFormInput
                            :id="`fApprovedQuantity-${index}`"
                            v-model="item.approved_quantity"
                            :disabled="state.busy"
                            :state="(errors.length > 0 ? false : null)"
                            :max="item.quantity"
                            placeholder="enter approval quantity here"
                            class="text-center"
                            type="number"
                            step="1"
                            min="0"
                          />
                          <div
                            v-if="(errors.length > 0)"
                            class="invalid-feedback"
                            v-text="errors[0]"
                          />
                        </BFormGroup>
                      </ValidationProvider>
                    </div>
                    <div
                      v-else
                      class="text-md-nowrap d-flex justify-content-center align-items-center min-h-35px"
                    >
                      <strong
                        :class="['font-weight-bolder', {
                          'text-danger': Number(item.approved_quantity) <= 0,
                          'text-warning': Number(item.approved_quantity) >= 1 && Number(item.quantity) !== Number(item.approved_quantity),
                          'text-success': Number(item.quantity) === Number(item.approved_quantity)
                        }]"
                        v-text="numberFormatter(item.approved_quantity, '0,0')"
                      />
                    </div>
                  </template>

                  <template #table-busy>
                    <div class="text-center py-5">
                      <BIcon
                        icon="stopwatch"
                        font-scale="5"
                        animation="cylon"
                      />
                      <p class="h3 py-2">
                        <strong>Loading . . .</strong>
                      </p>
                    </div>
                  </template>

                </BTable>
              </ValidationObserver>
            </BCol>
          </BRow>
        </BTab>

        <BTab class="p-2">
          <template #title>
            <div class="text-md-nowrap">
              <i class="fa-lg fad fa-file-lines mr-1" />
              <b class="is-tab-active">Delivery Details</b>
            </div>
          </template>
          <BRow class="mx-0 mt-1 mb-0">
            <BCol
              cols="12"
              class="px-1 px-md-2"
            >
              <BRow>
                <BCol
                  cols="12"
                  class="px-1 px-md-2"
                >
                  <BFormGroup
                    label-for="fCustomerDirect"
                    class="mb-1"
                  >
                    <BFormCheckbox
                      id="fCustomerDirect"
                      v-model="stockRequest.delivery_details.customer_direct"
                      name="customer_direct"
                      unchecked-value="0"
                      value="1"
                      disabled
                      switch
                    >
                      <strong>Direct Deliver to Customer?</strong>
                    </BFormCheckbox>
                  </BFormGroup>
                </BCol>
                <BCol
                  cols="12"
                  md="3"
                  class="px-1 px-md-2"
                >
                  <BFormGroup
                    label="Mobile Number"
                    label-for="fvCustomerMobile"
                    label-class="font-weight-bolder"
                    class="mb-2"
                  >
                    <BFormInput
                      id="fvCustomerMobile"
                      v-model="stockRequest.delivery_details.mobile_number"
                      placeholder="no customer mobile"
                      type="text"
                      disabled
                    />
                  </BFormGroup>
                </BCol>
                <BCol
                  cols="12"
                  md="9"
                  class="px-1 px-md-2"
                >
                  <BFormGroup
                    label="Name"
                    label-for="fvCustomerName"
                    label-class="font-weight-bolder"
                    class="mb-2"
                  >
                    <BFormInput
                      id="fvCustomerName"
                      v-model="stockRequest.delivery_details.customer"
                      placeholder="no customer name"
                      type="text"
                      disabled
                    />
                  </BFormGroup>
                </BCol>
                <BCol
                  cols="12"
                  class="px-1 px-md-2"
                >
                  <BFormGroup
                    label="Address"
                    label-for="fvCustomerAddress"
                    label-class="font-weight-bolder"
                    class="mb-2"
                  >
                    <BTextarea
                      id="fvCustomerAddress"
                      v-model="stockRequest.delivery_details.address"
                      placeholder="no customer address"
                      maxlength="50"
                      max-rows="6"
                      disabled
                      rows="4"
                    />
                  </BFormGroup>
                </BCol>

              </BRow>
            </BCol>
          </BRow>
        </BTab>

        <BTab
          v-if="!state.approval"
          class="p-2"
        >
          <template #title>
            <div class="text-md-nowrap">
              <i class="fa-lg fad fa-calendar-range mr-1" />
              <b class="is-tab-active ml-1">Delivery Schedule</b>
            </div>
          </template>
          <BRow class="mx-0 mt-1 mb-0">
            <BCol
              cols="12"
              class="px-1 px-md-2"
            >
              <BRow>

                <BCol
                  cols="12"
                  md="4"
                  class="px-1 px-md-2"
                >
                  <BFormGroup
                    label="Delivery Date"
                    label-for="fvScheduleDate"
                    label-class="font-weight-bolder"
                    class="mb-2"
                  >
                    <BFormInput
                      id="fvScheduleDate"
                      v-model="stockRequest.schedule_details.date"
                      type="date"
                      disabled
                    />
                  </BFormGroup>
                </BCol>

                <BCol
                  cols="12"
                  md="5"
                  class="px-1 px-md-2"
                >
                  <BFormGroup
                    label="Driver"
                    label-for="fvScheduleDriver"
                    label-class="font-weight-bolder"
                    class="mb-2"
                  >
                    <BFormInput
                      id="fvScheduleDriver"
                      v-model="stockRequest.schedule_details.driver_name"
                      type="text"
                      maxlength="50"
                      placeholder="no driver"
                      disabled
                    />
                  </BFormGroup>
                </BCol>

                <BCol
                  cols="12"
                  md="3"
                  class="px-1 px-md-2"
                >
                  <BFormGroup
                    label="Mobile Number"
                    label-for="fvScheduleMobile"
                    label-class="font-weight-bolder"
                    class="mb-2"
                  >
                    <BFormInput
                      id="fvScheduleMobile"
                      v-model="stockRequest.schedule_details.mobile_number"
                      type="text"
                      maxlength="50"
                      placeholder="no mobile number"
                      disabled
                    />
                  </BFormGroup>
                </BCol>

                <BCol
                  cols="12"
                  md="4"
                  class="px-1 px-md-2"
                />

                <BCol
                  cols="12"
                  md="8"
                  class="px-1 px-md-2"
                >
                  <BFormGroup
                    label="Helper"
                    label-for="fvScheduleHelper"
                    label-class="font-weight-bolder"
                    class="mb-2"
                  >
                    <BFormInput
                      id="fvScheduleHelper"
                      v-model="stockRequest.schedule_details.helper"
                      type="text"
                      maxlength="50"
                      placeholder="no helper"
                      disabled
                    />
                  </BFormGroup>
                </BCol>

                <BCol
                  cols="12"
                  md="4"
                  class="px-1 px-md-2"
                >
                  <BFormGroup
                    label="Vehicle Plate"
                    label-for="fvScheduleVehiclePlate"
                    label-class="font-weight-bolder"
                    class="mb-2"
                  >
                    <BFormInput
                      id="fvScheduleVehiclePlate"
                      v-model="stockRequest.schedule_details.vehicle_plate"
                      type="text"
                      maxlength="25"
                      placeholder="no vehicle plate"
                      disabled
                    />
                  </BFormGroup>
                </BCol>

                <BCol
                  cols="12"
                  md="8"
                  class="px-1 px-md-2"
                >
                  <BFormGroup
                    label="Vehicle Name"
                    label-for="fvScheduleVehicleName"
                    label-class="font-weight-bolder"
                    class="mb-2"
                  >
                    <BFormInput
                      id="fvScheduleVehicleName"
                      v-model="stockRequest.schedule_details.vehicle_name"
                      type="text"
                      maxlength="50"
                      placeholder="no helper"
                      disabled
                    />
                  </BFormGroup>
                </BCol>

              </BRow>
            </BCol>
          </BRow>
        </BTab>

        <BTab class="p-2 py-4">
          <template #title>
            <div class="text-md-nowrap">
              <i class="fa-lg fad fa-timeline-arrow mr-1" />
              <b class="is-tab-active ml-1">Tracking</b>
            </div>
          </template>

          <BContainer>
            <Timeline
              :stock-request="stockRequest"
            >
              <template #approvals="request">
                <Signatory
                  :stock-request="request"
                  :approver="requesterSignatory"
                />
                <Signatory
                  v-for="(approver, key) in request.approvals"
                  :key="key"
                  :stock-request="request"
                  :approver="approver"
                />
              </template>
            </Timeline>
          </BContainer>
        </BTab>

      </BTabs>
      <template #modal-footer="{ cancel }">
        <div class="flex-grow-1 d-flex justify-content-between mb-0 pb-0 gap-2">
          <div>
            <BButton
              v-if="state.approval"
              variant="outline-danger"
              size="sm"
              tabindex="-1"
              :disabled="state.busy"
              @click.prevent="$event => validateApproval($event, 'Disapproved')"
            >
              Disapprove
            </BButton>
          </div>
          <div class="d-flex gap-2">
            <BButton
              v-if="state.approval"
              variant="primary"
              size="sm"
              tabindex="-1"
              :disabled="state.busy"
              @click.prevent="$event => validateApproval($event, 'Approved')"
            >
              Approve
            </BButton>
            <BButton
              variant="outline-dark"
              tabindex="-1"
              size="sm"
              :disabled="state.busy"
              @click.prevent="$event => {
                $event.target.blur()
                cancel()
              }"
            >
              Close <span class="d-none d-md-inline-block">Window</span>
            </BButton>
          </div>
        </div>
      </template>
    </BModal>

  </BContainer>
</template>
<script>
import { mapGetters } from 'vuex'

import QUERY from 'lodash'
import MISC from '@/mixins/misc'
import FORMATTER from '@/mixins/formatter'

import ApprovalPending from '@/views/user/prd/purchase-orders/approvals/Pending.vue'
import ApprovalHistory from '@/views/user/prd/purchase-orders/approvals/History.vue'

import Timeline from '@/components/custom/timeline/Base.vue'
import Signatory from '@/components/custom/timeline/Signatory.vue'

import { SSharedList } from '@/services/shared'

export default {

  name: 'VProductionApproval',

  middleware: ['auth', 'prd'],

  metaInfo: () => ({
    title: 'Purchase Orders - Approvals'
  }),

  components: {
    ApprovalPending,
    ApprovalHistory,
    Timeline,
    Signatory
  },

  mixins: [
    MISC,
    FORMATTER
  ],

  data () {
    return {
      currentTabIndex: this.getCurrentTabIndexByURL(),
      state: {
        busy: false,
        approval: false,
        validStocks: false
      },
      fetching: {
        stocks: false
      },
      stockRequest: {
        id: '',
        stage: 0,
        references: {
          document_number: '',
          transfer_number: '',
          sap_doc_entry: '',
          transaction_number: ''
        },
        headers: {
          destination: '',
          destination_name: '',
          destination_code: '',
          origin: '',
          origin_name: '',
          origin_code: '',
          group: '',
          group_name: '',
          group_code: ''
        },
        delivery_details: {
          customer_direct: 0,
          customer: '',
          address: '',
          mobile_number: ''
        },
        schedule_details: {
          date: '',
          vehicle: '',
          vehicle_plate: '',
          vehicle_name: '',
          driver: '',
          driver_name: '',
          mobile_number: '',
          helper: ''
        },
        details: [],
        approvals: [],
        remarks: ''
      },
      requesterSignatory: {
        stage: 0,
        approver_label: 'Retail Officer - Origin',
        approver_action: 'Purchase Order',
        approver_name: '',
        last_seen: '',
        status: 'Requested',
        status_at: '',
        remarks: ''
      },
      tableProducts: {
        busy: false,
        filter: {
          search: ''
        },
        filterOn: [],
        stacked: this.isMobile(),
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 1, key: 'product_name', class: 'min-w-200px' },
          { mobile: 2, key: 'onhand_quantity', label: 'SAP Stocks', class: ['text-center', { 'col-2': !this.isMobile() }] },
          { mobile: 3, key: 'quantity', class: ['text-center', { 'col-2': !this.isMobile() }] },
          { mobile: 4, key: 'approved_quantity', label: 'Approved', class: ['text-center', { 'col-2': !this.isMobile() }] }
        ].sort(this.scaleOnMobile),
        histories: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 1, key: 'product_name', class: 'min-w-200px' },
          { mobile: 2, key: 'quantity', class: ['text-center', { 'col-2': !this.isMobile() }] },
          { mobile: 3, key: 'approved_quantity', label: 'Approved', class: ['text-center', { 'col-2': !this.isMobile() }] }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: {
    ...mapGetters({
      user: 'auth/user'
    })
  },

  methods: {

    getCurrentTabIndexByURL () {
      switch (this.$route.params.approver) {
        case 'histories' : {
          return 1
        }
        default: {
          return 0
        }
      }
    },

    async getStocks () {
      this.fetching.stocks = this.state.busy = true

      return SSharedList.getStocks({
        product_codes: this.stockRequest.details.map(
          detail => detail.product_code
        ),
        warehouse_code: this.stockRequest.headers.origin_code
      }).then(
        ({ data }) => {
          this.stockRequest.details = this.stockRequest.details.map(
            detail => {
              const product = QUERY.find(data, {
                product_code: detail.product_code
              })
              const quantity = parseInt(product.available_stock)

              return Object.assign(detail, {
                onhand_quantity: !isNaN(quantity) ? quantity : 0
              })
            }
          )
        }
      ).catch(() => {
        this.stockRequest.details = this.stockRequest.details.map(
          detail => Object.assign(detail, {
            onhand_quantity: 0
          })
        )
      }).finally(() => {
        this.$root.$emit('bv::hide::tooltip')
        this.fetching.stocks = this.state.busy = false
      })
    },

    showApproval (stockRequest, useApproval) {
      this.state.approval = useApproval

      const { id, approvable } = stockRequest
      const { delivery_details, schedule_details, transfer_details } = approvable

      this.stockRequest = Object.assign(this.stockRequest, {
        id: id,
        stage: approvable.stage,
        references: {
          document_number: approvable.document_number,
          transfer_number: approvable.transfer_number,
          sap_doc_entry: approvable.sap_doc_entry,
          transaction_number: approvable.transaction_number
        },
        headers: {
          destination: approvable.destination_id,
          destination_name: approvable.destination_name,
          destination_code: approvable.destination_code,
          origin: approvable.origin_id,
          origin_code: approvable.origin_code,
          origin_name: approvable.origin_name,
          group: approvable.group_id,
          group_code: approvable.group_code,
          group_name: approvable.group_name
        },
        delivery_details: {
          customer_direct: Number(approvable.customer_direct),
          customer: delivery_details.customer,
          mobile_number: delivery_details.mobile_number,
          address: delivery_details.address
        },
        schedule_details: {
          date: schedule_details?.date || '',
          vehicle: schedule_details?.vehicle || '',
          vehicle_plate: schedule_details?.vehicle_plate || '',
          vehicle_name: schedule_details?.vehicle_name || '',
          driver: schedule_details?.driver || '',
          driver_name: schedule_details?.driver_name || '',
          mobile_number: schedule_details?.mobile_number || '',
          helper: schedule_details?.helper || ''
        },
        details: (
          useApproval ? transfer_details.map(
            product => Object.assign(product, {
              onhand_quantity: 0,
              approved_quantity: product.quantity
            })
          ) : approvable.transfer_details
        ),
        approvals: approvable.approvals,
        remarks: approvable.remarks,
        created_at: approvable.created_at
      })

      this.requesterSignatory = Object.assign(this.requesterSignatory, {
        approver_name: approvable.destination_name,
        status_at: approvable.created_at
      })

      this.$bvModal.show('modalApproval')
    },

    validateApproval ({ target }, status = 'Approved') {
      this.$refs.formProducts.validate().then(
        validProducts => {
          if (!validProducts) {
            return this.swalInvalid()
          }

          const allInputsAreZero = (QUERY.filter(
            this.$refs.tableProducts.localItems,
            product => (Number(product.approved_quantity) <= 0)
          ).length) === (this.$refs.tableProducts.localItems.length)

          if (allInputsAreZero) {
            return this.swalInvalid('<h6>All your inputs are filled with zero value, Disapprove it instead.</h6>', 'Notice')
          }

          this.swalConfirmWithRemarks({
            html: `${target.innerText} Purchase Order?`,
            remarksRequired: status === 'Disapproved',
            customClass: {
              confirmButton: (
                status === 'Disapproved' ? (
                  'btn btn-sm btn-danger'
                ) : 'btn btn-sm btn-primary'
              )
            },
            preConfirm: remarks => {
              return this.$refs.saPending.update({
                id: this.stockRequest.id,
                details: this.stockRequest.details.map(
                  ({ id, quantity, approved_quantity }) => ({
                    id,
                    quantity,
                    approved_quantity
                  })
                ),
                remarks,
                status
              })
            },
            confirmButtonText: target.innerText
          })
        }
      )
    }

  }
}
</script>
